import React from 'react';
import { createUseStyles } from 'react-jss';

import classNames from 'classnames';
import { Theme } from '@app/theme';
import { useParticipantFieldsByName } from '@app/api';
import { useTranslation } from 'react-i18next';
import { toPercent } from 'lib/numbers';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    backgroundColor: theme.colors.grayBackground,
    borderRadius: theme.borderRadius,
    padding: theme.marginBase * 2,
  },
  clickable: {
    opacity: 0.7,
    cursor: 'pointer',
    border: {
      color: theme.colors.grayBorder,
      style: 'solid',
      width: 1,
    },
  },
  selected: {
    opacity: 1,
    borderColor: theme.colors.greenAccessible,
  },
  title: {
    ...theme.fonts.h5,
  },
  details: {
    ...theme.fonts.body,
  },
}));

interface Props {
  config: { criteriaWeightWhitelist: string[]; size: number };
  totalToInviteDaily?: number;
  onClick?: (criteriaWeightWhitelist?: string[]) => any;
  selectedWhiteList?: string[];
}

export const QueueSearchConfig = ({
  config,
  totalToInviteDaily,
  onClick,
  selectedWhiteList,
}: Props) => {
  const classes = useStyles();
  const participantFieldsByName = useParticipantFieldsByName();
  const { t } = useTranslation();

  const selected = React.useMemo(() => {
    if (!config || !selectedWhiteList) return false;
    if (config.criteriaWeightWhitelist.length !== selectedWhiteList.length)
      return false;
    return config.criteriaWeightWhitelist.every((value) =>
      selectedWhiteList.includes(value),
    );
  }, [config, selectedWhiteList]);

  const handleClick = React.useCallback(() => {
    if (!onClick) {
      return;
    }

    if (selected) {
      onClick(undefined);
      return;
    }

    onClick(config.criteriaWeightWhitelist);
  }, [onClick, config, selected]);

  return (
    <div
      className={classNames(classes.container, {
        [classes.clickable]: !!onClick,
        [classes.selected]: selected,
      })}
      onClick={handleClick}
    >
      <h4 className={classes.title}>
        {config.criteriaWeightWhitelist
          .map((c) => participantFieldsByName?.[c]?.label || c)
          .join(', ')}
      </h4>
      <div className={classes.details}>
        {t('sessions.details.invitationQueue.searchParticipants', {
          count: config.size,
          percent: toPercent(config.size / (totalToInviteDaily || 1)),
        })}
      </div>
    </div>
  );
};
